import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Users, Plus } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClientsList({ user, onLogout }) {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [showInviteLink, setShowInviteLink] = useState(false);
  const [publicInviteLink, setPublicInviteLink] = useState('');
  const [generatingPublicLink, setGeneratingPublicLink] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    trial_period: 'unlimited', // 'unlimited' or '7_days'
  });

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = clients.filter(client =>
        `${client.first_name} ${client.last_name} ${client.email}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
      setFilteredClients(filtered);
    } else {
      setFilteredClients(clients);
    }
  }, [searchTerm, clients]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/coach/clients`);
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'success',
      pending: 'warning',
      expired: 'danger'
    };
    return <Badge className={`badge-${variants[status] || 'info'}`}>{status}</Badge>;
  };

  const handleSelectAll = () => {
    if (selectedClients.length === filteredClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map(c => c.id));
    }
  };

  const handleSelectClient = (clientId) => {
    if (selectedClients.includes(clientId)) {
      setSelectedClients(selectedClients.filter(id => id !== clientId));
    } else {
      setSelectedClients([...selectedClients, clientId]);
    }
  };

  const handleBulkDelete = async () => {
    console.log('handleBulkDelete called with selectedClients:', selectedClients);
    
    if (selectedClients.length === 0) {
      toast.error('No clients selected');
      return;
    }

    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setShowDeleteConfirm(false);
    setDeleting(true);
    
    try {
      console.log('Sending delete request for clients:', selectedClients);
      const response = await axios.post(`${API}/coach/clients/bulk-delete`, {
        client_ids: selectedClients
      });
      console.log('Delete response:', response.data);
      
      toast.success(`${selectedClients.length} client(s) deleted successfully`);
      setSelectedClients([]);
      await fetchClients();
    } catch (error) {
      console.error('Delete error:', error.response?.data || error);
      toast.error(error.response?.data?.detail || 'Failed to delete clients');
    } finally {
      setDeleting(false);
    }
  };

  // Impersonate client - opens client portal in new tab as if logged in as that client
  const impersonateClient = async (clientId, targetPath = '/app') => {
    console.log('[ClientsList] Impersonating client:', clientId, 'target:', targetPath);
    
    try {
      // Call impersonation endpoint (coach token is automatically included via axios interceptor)
      const response = await axios.post(`${API}/auth/impersonate/${clientId}`);
      
      console.log('[ClientsList] Impersonation successful for:', response.data.user?.email);
      
      if (response.data.access_token) {
        // MOBILE-FRIENDLY FIX: Pass token directly in URL to avoid localStorage race conditions
        // This is more reliable on iOS Safari and mobile browsers
        const token = response.data.access_token;
        const userData = encodeURIComponent(JSON.stringify(response.data.user));
        
        // Build URL with token parameters
        const separator = targetPath.includes('?') ? '&' : '?';
        const urlWithToken = `${targetPath}${separator}imp_token=${token}&imp_user=${userData}`;
        
        console.log('[ClientsList] Opening client portal with token in URL...');
        
        // Open the client portal in a new tab with token in URL
        const newWindow = window.open(urlWithToken, '_blank');
        
        if (newWindow) {
          toast.success(`✅ Opening ${response.data.user?.first_name || 'client'}'s dashboard...`);
        } else {
          // Popup blocked - try same-window navigation as fallback for mobile
          toast.info('Opening client dashboard...');
          window.location.href = urlWithToken;
        }
      }
    } catch (error) {
      console.error('[ClientsList] Impersonation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to open client dashboard');
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        email: formData.email,
        is_trial: formData.trial_period === '7_days',
        trial_days: formData.trial_period === '7_days' ? 7 : null
      };
      
      const response = await axios.post(`${API}/coach/invite-client`, payload);
      
      const link = `${window.location.origin}/app/signup?token=${response.data.invitation_token}`;
      setInviteLink(link);
      setShowInviteLink(true);
      toast.success('Signup link generated!');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invitation');
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink).then(() => {
      toast.success('Link copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  const closeInviteDialog = () => {
    setInviteOpen(false);
    setShowInviteLink(false);
    setInviteLink('');
    setFormData({
      email: '',
      trial_period: 'unlimited'
    });
  };

  if (loading) {
    return <div data-testid="loading">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="page-container">
        {/* Three-column layout for client options */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          
          {/* 1. Add Client - Coach types in client info */}
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="pt-6">
              <h3 className="font-semibold text-gray-900 mb-2">👤 Add Client</h3>
              <p className="text-sm text-gray-600 mb-4">
                Manually enter a client's info. They'll receive login credentials.
              </p>
              <Dialog open={inviteOpen} onOpenChange={(open) => {
                if (!open) closeInviteDialog();
                else setInviteOpen(true);
              }}>
                <DialogTrigger asChild>
                  <Button className="w-full bg-[#1a61e8] hover:bg-[#1552c7]">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Client
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Client</DialogTitle>
                    <DialogDescription>
                      Enter client's email and generate their signup link.
                    </DialogDescription>
                  </DialogHeader>
                  
                  {!showInviteLink ? (
                    <form onSubmit={handleInvite} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="client@example.com"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="trial_period">Access Period</Label>
                        <Select
                          value={formData.trial_period}
                          onValueChange={(value) => setFormData({...formData, trial_period: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unlimited">Unlimited (Standard Client)</SelectItem>
                            <SelectItem value="7_days">7-Day Free Trial</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                        <p className="text-blue-800">
                          📧 A signup link will be generated. The email will be locked to prevent sharing.
                        </p>
                      </div>
                      
                      <Button type="submit" className="w-full bg-[#1a61e8] hover:bg-[#1552c7]">
                        Generate Signup Link
                      </Button>
                    </form>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-800 mb-2">
                          ✅ Signup link created for {formData.email}
                        </p>
                        <p className="text-xs text-green-700">
                          {formData.trial_period === '7_days' ? '7-day trial access' : 'Standard client access'}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Client Signup Link</Label>
                        <div className="flex gap-2">
                          <Input
                            value={inviteLink}
                            readOnly
                            className="font-mono text-sm"
                          />
                          <Button onClick={copyInviteLink}>
                            Copy
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500">
                          ⚠️ This link is locked to {formData.email} and cannot be used by others.
                        </p>
                      </div>
                      <Button onClick={closeInviteDialog} variant="outline" className="w-full">
                        Done
                      </Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          {/* 3. Signup Link - For landing page */}
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardContent className="pt-6">
              <h3 className="font-semibold text-gray-900 mb-2">🔗 Signup Link</h3>
              <p className="text-sm text-gray-600 mb-4">
                Share this link with clients. They sign up directly without payment.
              </p>
              <div className="space-y-2">
                {publicInviteLink ? (
                  <>
                    <Input
                      value={publicInviteLink}
                      readOnly
                      className="bg-white text-sm font-mono"
                    />
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(publicInviteLink).then(() => {
                          toast.success('Signup link copied!');
                        }).catch(() => {
                          const textArea = document.createElement('textarea');
                          textArea.value = publicInviteLink;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                          toast.success('Signup link copied!');
                        });
                      }}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      Copy Signup Link
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={async () => {
                      setGeneratingPublicLink(true);
                      try {
                        const response = await axios.post(`${API}/invite-links`, {
                          client_name: null
                        });
                        const link = `${window.location.origin}/signup/${response.data.invite_link}`;
                        setPublicInviteLink(link);
                        toast.success('Signup link created!');
                      } catch (error) {
                        toast.error('Failed to create signup link');
                      } finally {
                        setGeneratingPublicLink(false);
                      }
                    }}
                    disabled={generatingPublicLink}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {generatingPublicLink ? 'Generating...' : 'Generate Signup Link'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="clients-list-title">
              All Clients
            </h1>
            <p className="text-lg text-gray-600">
              Managing {clients.length} client{clients.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Search */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex gap-4 items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <Input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="client-search-input"
                />
              </div>
              {selectedClients.length > 0 && (
                <Button 
                  type="button"
                  variant="destructive" 
                  onClick={handleBulkDelete}
                  disabled={deleting}
                  data-testid="delete-selected-button"
                >
                  Delete {selectedClients.length} Selected
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Clients Grid */}
        {filteredClients.length > 0 && (
          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedClients.length === filteredClients.length}
                onChange={handleSelectAll}
                className="w-4 h-4 cursor-pointer"
              />
              <span className="text-sm font-medium">Select All ({filteredClients.length})</span>
            </label>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="clients-grid">
          {filteredClients.length === 0 ? (
            <div className="col-span-full">
              <Card>
                <CardContent className="pt-6 text-center">
                  <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">
                    {searchTerm ? 'No clients found' : 'No clients yet'}
                  </p>
                </CardContent>
              </Card>
            </div>
          ) : (
            filteredClients.map((client, index) => (
              <div key={client.id} className="relative" data-testid={`client-card-${index}`}>
                <input
                  type="checkbox"
                  checked={selectedClients.includes(client.id)}
                  onChange={() => handleSelectClient(client.id)}
                  className="absolute top-4 left-4 w-5 h-5 z-10 cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                />
                <Card className="hover:shadow-lg transition-shadow h-full pl-10">
                  <Link to={`/coach/client/${client.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-xl">
                            {client.first_name} {client.last_name}
                            {client.email?.startsWith('test_') && (
                              <Badge className="ml-2 bg-green-100 text-green-700">Test</Badge>
                            )}
                            {client.is_trial && (
                              client.trial_expired ? (
                                <Badge className="ml-2 bg-red-100 text-red-700 border border-red-300">
                                  Trial Expired
                                </Badge>
                              ) : (
                                <Badge className="ml-2 bg-orange-100 text-orange-700">
                                  {client.trial_days_remaining !== undefined 
                                    ? `Trial: ${client.trial_days_remaining} days left`
                                    : 'Trial'}
                                </Badge>
                              )
                            )}
                          </CardTitle>
                          <CardDescription className="mt-1">{client.email}</CardDescription>
                        </div>
                        {getStatusBadge(client.status)}
                      </div>
                    </CardHeader>
                  </Link>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      {client.current_weight && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Current Weight:</span>
                          <span className="font-semibold text-gray-900">{client.current_weight} kg</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Joined:</span>
                        <span className="font-medium">
                          {new Date(client.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      
                      {/* Trial User - Send Subscription Link (Always Available) */}
                      {client.is_trial && (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                          <p className="text-sm text-blue-800 font-medium">
                            {client.trial_expired 
                              ? '⚠️ Trial Expired - Send Subscription' 
                              : '💎 Trial User - Upgrade Available'}
                          </p>
                          <Button
                            data-testid={`send-subscription-link-${client.id}`}
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              try {
                                await axios.post(`${API}/coach/send-subscription-link/${client.id}`);
                                toast.success(`Subscription link sent to ${client.email}!`);
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to send link');
                              }
                            }}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                            size="sm"
                          >
                            📧 Send Subscription Link
                          </Button>
                          <Button
                            data-testid={`activate-subscription-${client.id}`}
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              try {
                                const response = await axios.post(`${API}/coach/activate-subscription/${client.id}`);
                                if (response.data.status === 'success') {
                                  toast.success(`✅ ${response.data.message}`);
                                  fetchClients();
                                } else if (response.data.status === 'already_active') {
                                  toast.info(response.data.message);
                                  fetchClients();
                                } else {
                                  toast.warning(response.data.message);
                                }
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to activate subscription');
                              }
                            }}
                            className="w-full bg-green-600 hover:bg-green-700 text-white"
                            size="sm"
                            variant="outline"
                          >
                            ✅ Activate Subscription (if already paid)
                          </Button>
                          <p className="text-xs text-gray-600">
                            Send the link to your client. After they pay, click "Activate" if their status doesn't update automatically.
                          </p>
                        </div>
                      )}
                      
                      {client.email?.startsWith('test_') && (
                        <div className="space-y-2 mt-2">
                          <Button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              impersonateClient(client.id, '/app');
                            }}
                            className="w-full bg-green-600 hover:bg-green-700"
                            size="sm"
                          >
                            Open Dashboard
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              impersonateClient(client.id, '/app/checkin');
                            }}
                            className="w-full bg-blue-600 hover:bg-blue-700"
                            size="sm"
                            variant="outline"
                          >
                            Submit Check-in
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedClients.length} client(s)? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-4 justify-end mt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
